import { useAppSelector } from "@app/hooks";
import {
  createHeightBasedPages,
  PageData,
} from "@survey-preview/utils/heightPagination";
import { generatePages } from "@survey-preview/utils/pageGeneration";
import { processPropertyFields } from "@survey-preview/utils/propertyFieldUtils";
import { processLeftColumnContent } from "@survey-preview/utils/propertyProcessing";
import { Property } from "@survey-types/survey.property.types";
import {
  selectCurrentSurvey,
  selectVisibleProperties,
} from "@survey/surveySelectors";
import PropertyFlyerPreview from "./PropertyFlyerPreview";
import PropertyImagePreview from "./PropertyImagePreview";
import PropertyPage from "./PropertyPage";
import PropertySitePlanPreview from "./PropertySitePlanPreview";

interface PropertyDetailsPreviewProps {
  mode?: string;
}

interface ProcessedProperty {
  property: Property;
  index: number;
  pages: PageData[];
  propertyId: string;
}

const PropertyDetailsPreview = (props: PropertyDetailsPreviewProps) => {
  const { mode } = props;

  const survey = useAppSelector(selectCurrentSurvey);
  const visibleProperties = useAppSelector(selectVisibleProperties);

  // Early return if no properties
  if (visibleProperties.length === 0) {
    return null;
  } // Simple boolean flags
  const isLandscape = survey?.pdfPaperSize === "Landscape";
  const includePhotos = Boolean(survey?.isIncludePhotos);
  const includeSitePlans = Boolean(survey?.isIncludeSitePlans);
  const includeFlyers = Boolean(survey?.isIncludeFlyers);

  // Check if all attributes in propertyFieldRuleList are hidden
  const propertyFieldRules =
    survey?.surveyPropertyFieldRule?.propertyFieldRuleList || [];
  const hasVisibleFields = propertyFieldRules.some((rule) => !rule.isHidden);

  // Process properties
  const processedProperties: ProcessedProperty[] = visibleProperties.map(
    (property, index) => {
      const propertyFields = processPropertyFields(property, survey);
      const leftColumnContent = processLeftColumnContent(
        property,
        isLandscape,
        survey,
      );
      const propertyFieldPages = createHeightBasedPages(propertyFields, {
        isLandscape,
      });
      const pages = generatePages(
        leftColumnContent,
        propertyFieldPages,
        isLandscape,
      );

      return {
        property,
        index,
        pages,
        propertyId: property.propertyId,
      };
    },
  );

  // Render function for property details
  const renderPropertyDetails = (processedProperty: ProcessedProperty) => {
    const { property, index, pages } = processedProperty;

    // Don't render property pages if all attributes are hidden
    if (!hasVisibleFields) {
      return null;
    }

    return pages.map((page: PageData, pageIndex: number) => (
      <PropertyPage
        key={`property-${index}-page-${pageIndex}`}
        page={page}
        pageIndex={pageIndex}
        property={property}
        propertyIndex={index}
        survey={survey}
        mode={mode}
      />
    ));
  };

  return (
    <>
      {processedProperties.map((processedProperty) => {
        const { propertyId } = processedProperty;

        return (
          <div key={`property-${propertyId}`} className="flex flex-col gap-y-4">
            {renderPropertyDetails(processedProperty)}
            {includePhotos && (
              <PropertyImagePreview mode={mode} propertyFilter={propertyId} />
            )}
            {includeSitePlans && (
              <PropertySitePlanPreview
                mode={mode}
                propertyFilter={propertyId}
              />
            )}
            {includeFlyers && (
              <PropertyFlyerPreview propertyFilter={propertyId} />
            )}
          </div>
        );
      })}
    </>
  );
};

export default PropertyDetailsPreview;
